import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ArrowLeft, BadgeCheck, CalendarClock, CheckCircle2, Coffee, Fingerprint, Paperclip, PenLine,
} from "lucide-react";
import { toast } from "sonner";
import { api, fmtApiError, fmtFecha } from "@/lib/api";
import VisorArchivo from "@/components/VisorArchivo";
import { hoyISOBA } from "@/components/ClockBA";
import {
  Avatar, Badge, Card, MesNav, Modal, btnPrim, btnSec, inputCls,
} from "@/components/rrhh/ui";

const ESTADOS_AJUSTE = [
  ["completa", "Completa"], ["incompleta", "Incompleta"], ["injustificada", "Falta injustificada"],
  ["justificada", "Justificada"], ["vacaciones", "Vacaciones"], ["licencia", "Licencia"],
  ["feriado", "Feriado"], ["franco_trabajado", "Franco trabajado"],
];
const CHIP_JUST = { aprobado: "verde", pendiente: "amarillo", rechazado: "rojo" };

const fmtNum = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

export default function DetallePersonaPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const mes = params.get("mes") || hoyISOBA().slice(0, 7);
  const setMes = (v) => setParams({ mes: v }, { replace: true });
  const [ajustar, setAjustar] = useState(null); // fila del día
  const [visor, setVisor] = useState(null);

  const { data } = useQuery({
    queryKey: ["liq-persona", id, mes],
    queryFn: async () => (await api.get(`/liquidacion/persona/${id}`, { params: { mes } })).data,
  });

  const mutRevisado = useMutation({
    mutationFn: async (revisado) => api.post("/liquidacion/revisado", { empleado_id: id, mes, revisado }),
    onSuccess: (_r, revisado) => {
      qc.invalidateQueries({ queryKey: ["liq-persona", id] });
      qc.invalidateQueries({ queryKey: ["liq-resumen"] });
      toast.success(revisado ? "Mes marcado como revisado" : "Revisión desmarcada");
    },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["liq-persona", id] });
    qc.invalidateQueries({ queryKey: ["liq-resumen"] });
    qc.invalidateQueries({ queryKey: ["liq-ajustes"] });
  };

  const verComprobante = async (j) => {
    setVisor(j.archivo_url);
    try {
      await api.post("/rrhh/registrar-vista", { que: "comprobante", referencia: j.archivo_url, empleado_id: id });
    } catch { /* el registro no bloquea */ }
  };

  if (!data) {
    return <div className="py-16 text-center text-sm text-gray-500" data-testid="liq-persona-cargando">Cargando detalle…</div>;
  }
  const e = data.empleado;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} data-testid="pagina-liq-persona">
      <button onClick={() => navigate(`/liquidacion/resumen?mes=${mes}`)} className="mb-4 flex items-center gap-1.5 text-sm font-medium text-gray-400 transition-colors hover:text-white" data-testid="liq-volver">
        <ArrowLeft size={15} /> Volver a la liquidación
      </button>

      <Card testid="liq-perfil" className="mb-5">
        <div className="flex flex-wrap items-center gap-4">
          <Avatar iniciales={e.iniciales} size="xl" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-white">{e.nombre} {e.apellido}</h1>
            <p className="mt-0.5 text-sm text-gray-400">
              <span className="font-mono-num">Legajo {e.legajo}</span> · Local base: <span className="uppercase">{e.local_base}</span>
            </p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {data.ajustes_mes > 0 && <Badge color="amarillo" testid="liq-ajustes-badge"><PenLine size={11} className="mr-1 inline" /> {data.ajustes_mes} ajuste{data.ajustes_mes === 1 ? "" : "s"}</Badge>}
            <button
              className={data.revisado ? btnSec : `${btnPrim} flex items-center gap-2`}
              disabled={mutRevisado.isPending}
              onClick={() => mutRevisado.mutate(!data.revisado)}
              data-testid="btn-revisado"
            >
              {data.revisado ? <><CheckCircle2 size={14} className="mr-1.5 inline text-[#2DD4BF]" /> Revisado — desmarcar</> : "Marcar como revisado"}
            </button>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 border-t border-white/8 pt-5 lg:grid-cols-3">
          <DatoLiq icon={CalendarClock} label="Horario semanal" valor={`Lun a sáb · ${e.horario_entrada} a ${e.horario_salida}`} />
          <DatoLiq icon={Coffee} label="Pausas y tolerancia" valor="50 min de pausas · 5 min de tolerancia" />
          <DatoLiq icon={BadgeCheck} label="Alta" valor={<span className="font-mono-num">{fmtFecha(e.fecha_alta)}</span>} />
        </div>
      </Card>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-white">Detalle diario</h2>
        <MesNav mes={mes} onChange={setMes} testid="nav-mes-persona" />
      </div>

      <Card testid="card-detalle-diario" subtitulo="" bodyCls="p-0" titulo={`${data.mes_label} · una fila por día`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8 text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3 font-semibold">Día</th>
                <th className="px-3 py-3 font-semibold">Local</th>
                <th className="px-3 py-3 font-semibold">Entrada</th>
                <th className="px-3 py-3 font-semibold">Pausas</th>
                <th className="px-3 py-3 font-semibold">Salida</th>
                <th className="px-3 py-3 font-semibold">Tardanza</th>
                <th className="px-3 py-3 font-semibold">Estado</th>
                <th className="px-3 py-3 text-right font-semibold">Comunes</th>
                <th className="px-3 py-3 font-semibold">Adicional</th>
                <th className="px-3 py-3 font-semibold">Justificativo</th>
                <th className="px-3 py-3 font-semibold">Observaciones</th>
                <th className="px-4 py-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {data.dias.map((r) => {
                const sinDatos = !r.estado && !r.entrada;
                const apagado = r.fuera_alta || (r.futuro && sinDatos);
                const sombreado = (r.domingo || r.feriado) && !apagado;
                return (
                  <tr
                    key={r.fecha}
                    className={`border-b border-white/5 last:border-0 ${apagado ? "opacity-35" : ""} ${sombreado ? "bg-white/[0.025]" : ""}`}
                    data-testid={`dia-${r.fecha}`}
                  >
                    <td className="whitespace-nowrap px-4 py-2.5 font-mono-num text-xs text-gray-300">{fmtFecha(r.fecha)} <span className="text-gray-600">{r.dia.slice(-1)}</span></td>
                    <td className="px-3 py-2.5 text-xs text-gray-400">{r.local}</td>
                    <td className="px-3 py-2.5 font-mono-num text-xs">
                      {r.entrada ? <span className={r.tardanza_min > 0 ? "text-amber-300" : "text-[#2DD4BF]"}>{r.entrada}</span> : <span className="text-gray-600">--:--</span>}
                    </td>
                    <td className="px-3 py-2.5 font-mono-num text-xs text-gray-400">{r.pausas_min ? `${r.pausas_min} min` : "—"}</td>
                    <td className="px-3 py-2.5 font-mono-num text-xs">
                      {r.salida ? <span className="text-white">{r.salida}</span> : <span className="text-gray-600">--:--</span>}
                    </td>
                    <td className="px-3 py-2.5 font-mono-num text-xs">
                      {r.tardanza_min > 0 ? <span className="text-amber-300">{r.tardanza_min} min</span> : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      {r.estado_label
                        ? <span className="inline-flex items-center gap-1.5">
                            <Badge color={r.estado_color}>{r.estado_label}</Badge>
                            {r.ajustada && <PenLine size={11} className="text-[#2DD4BF]" data-testid={`lapiz-${r.fecha}`} />}
                          </span>
                        : <span className="text-xs text-gray-600">{r.fuera_alta ? "Fuera del alta" : r.domingo ? "Franco" : "—"}</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right font-mono-num text-xs">
                      {r.ajustada && r.auto?.horas_comunes != null && (
                        <s className="mr-1.5 text-gray-600">{fmtNum(r.auto.horas_comunes)}</s>
                      )}
                      <span className={r.ajustada ? "font-semibold text-[#2DD4BF]" : r.horas_comunes ? "text-white" : "text-gray-600"}>
                        {fmtNum(r.horas_comunes)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                      {r.ajustada && r.auto?.hora_adicional != null && (
                        <s className="mr-1.5 text-gray-600">{r.auto.hora_adicional ? "Sí" : "No"}</s>
                      )}
                      {r.hora_adicional
                        ? <span className={r.ajustada ? "font-semibold text-[#2DD4BF]" : "text-[#2DD4BF]"}>✓</span>
                        : <span className="text-gray-500">✗{r.motivo_sin_adicional ? <span className="ml-1 text-gray-500">{r.motivo_sin_adicional}</span> : null}</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5">
                      {r.justificativo
                        ? <span className="flex items-center gap-1.5">
                            <Badge color={CHIP_JUST[r.justificativo.estado]}>{r.justificativo.tipo_label}</Badge>
                            {r.justificativo.archivo_url && (
                              <button onClick={() => verComprobante(r.justificativo)} className="text-xs font-semibold text-[#2DD4BF] hover:underline" data-testid={`just-link-${r.fecha}`}>
                                <Paperclip size={11} className="mr-0.5 inline" /> Ver
                              </button>
                            )}
                          </span>
                        : <span className="text-gray-700">—</span>}
                    </td>
                    <td className="max-w-52 px-3 py-2.5 text-[11px] leading-snug text-gray-500">{r.observaciones || "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        className={`${btnSec} !px-2.5 !py-1 text-xs`}
                        disabled={apagado || (r.futuro && !r.entrada && !r.estado)}
                        onClick={() => setAjustar(r)}
                        data-testid={`ajustar-${r.fecha}`}
                      >
                        <PenLine size={12} className="mr-1 inline" /> Ajustar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {ajustar && (
        <ModalAjuste
          empleado={e}
          dia={ajustar}
          mes={mes}
          onClose={() => setAjustar(null)}
          onOk={() => { setAjustar(null); invalidar(); }}
        />
      )}

      {visor && <VisorArchivo url={visor} onClose={() => setVisor(null)} />}
    </motion.div>
  );
}

function DatoLiq({ icon: Icon, label, valor }) {
  return (
    <div className="flex items-start gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10 text-gray-400">
        <Icon size={16} />
      </span>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{label}</p>
        <div className="mt-1 text-sm font-medium text-white">{valor}</div>
      </div>
    </div>
  );
}

function ModalAjuste({ empleado, dia, mes, onClose, onOk }) {
  const [horas, setHoras] = useState(String(dia.horas_comunes));
  const [adicional, setAdicional] = useState(dia.hora_adicional ? "si" : "no");
  const [estado, setEstado] = useState(dia.estado && ESTADOS_AJUSTE.some(([c]) => c === dia.estado) ? dia.estado : "");
  const [observacion, setObservacion] = useState(dia.observaciones || "");
  const [justificacion, setJustificacion] = useState("");
  const [fmTipo, setFmTipo] = useState("salida");
  const [fmHora, setFmHora] = useState("");
  const [fmMotivo, setFmMotivo] = useState("");

  const mutAjuste = useMutation({
    mutationFn: async () => api.post("/liquidacion/ajustes", {
      empleado_id: empleado.id, fecha: dia.fecha,
      horas_comunes: horas === "" ? null : Number(horas),
      hora_adicional: adicional === "" ? null : adicional === "si",
      estado: estado || null,
      observacion: observacion.trim(),
      justificacion: justificacion.trim(),
    }),
    onSuccess: () => { toast.success("Ajuste guardado — pisa el cálculo automático"); onOk(); },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  const mutFichada = useMutation({
    mutationFn: async () => api.post("/liquidacion/fichada-manual", {
      empleado_id: empleado.id, fecha: dia.fecha, tipo: fmTipo, hora: fmHora, motivo: fmMotivo.trim(),
    }),
    onSuccess: () => { toast.success("Fichada manual cargada — el día se recalculó"); onOk(); },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  return (
    <Modal
      titulo={`Ajustar el ${fmtFecha(dia.fecha)} — ${empleado.apellido}, ${empleado.nombre}`}
      onClose={onClose}
      ancho="max-w-xl"
      testid="modal-ajuste"
    >
      <div className="mb-4 rounded-xl border border-white/8 bg-[#0B0F14] px-4 py-3 text-sm" data-testid="ajuste-auto">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Cálculo automático</p>
        <p className="mt-1 text-gray-300">
          <span className="font-mono-num">{fmtNum(dia.horas_comunes)} h comunes</span> ·{" "}
          {dia.hora_adicional ? "con adicional" : `sin adicional${dia.motivo_sin_adicional ? ` (${dia.motivo_sin_adicional})` : ""}`} ·{" "}
          {dia.estado_label || "sin jornada"}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Horas comunes</label>
          <input type="number" min="0" max="12" step="0.5" value={horas} onChange={(e) => setHoras(e.target.value)} className={`${inputCls} w-full font-mono-num`} data-testid="ajuste-horas" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Hora adicional</label>
          <select value={adicional} onChange={(e) => setAdicional(e.target.value)} className={`${inputCls} w-full`} data-testid="ajuste-adicional">
            <option value="si">Sí</option>
            <option value="no">No</option>
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Estado del día</label>
          <select value={estado} onChange={(e) => setEstado(e.target.value)} className={`${inputCls} w-full`} data-testid="ajuste-estado">
            <option value="">Sin cambiar</option>
            {ESTADOS_AJUSTE.map(([cod, label]) => <option key={cod} value={cod}>{label}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-3.5">
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Observación</label>
        <input value={observacion} onChange={(e) => setObservacion(e.target.value)} placeholder="Se muestra en el detalle del día" className={`${inputCls} w-full`} data-testid="ajuste-observacion" />
      </div>
      <div className="mt-3.5">
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Justificación (obligatoria)</label>
        <textarea
          value={justificacion}
          onChange={(e) => setJustificacion(e.target.value)}
          rows={2}
          placeholder="ej. me lo confirma el encargado por escrito"
          className={`${inputCls} w-full resize-none`}
          data-testid="ajuste-justificacion"
        />
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className={btnSec} onClick={onClose}>Cancelar</button>
        <button
          className={btnPrim}
          disabled={mutAjuste.isPending || justificacion.trim().length < 3}
          onClick={() => mutAjuste.mutate()}
          data-testid="ajuste-guardar"
        >
          Guardar el ajuste
        </button>
      </div>

      <div className="mt-6 border-t border-white/8 pt-5">
        <p className="flex items-center gap-2 text-sm font-semibold text-white"><Fingerprint size={15} className="text-[#2DD4BF]" /> ¿Faltó una fichada?</p>
        <p className="mb-3 mt-1 text-xs text-gray-500">Cargá la entrada o la salida que faltó. Queda con origen manual y entra como ajuste.</p>
        <div className="grid grid-cols-[110px_110px_1fr_auto] items-end gap-2">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-500">Tipo</label>
            <select value={fmTipo} onChange={(e) => setFmTipo(e.target.value)} className={`${inputCls} w-full`} data-testid="fm-tipo">
              <option value="entrada">Entrada</option>
              <option value="salida">Salida</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-500">Hora</label>
            <input type="time" value={fmHora} onChange={(e) => setFmHora(e.target.value)} className={`${inputCls} w-full font-mono-num`} data-testid="fm-hora" />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-500">Motivo</label>
            <input value={fmMotivo} onChange={(e) => setFmMotivo(e.target.value)} placeholder="obligatorio" className={`${inputCls} w-full`} data-testid="fm-motivo" />
          </div>
          <button
            className={btnSec}
            disabled={mutFichada.isPending || !fmHora || fmMotivo.trim().length < 3 || (fmTipo === "entrada" && !!dia.entrada) || (fmTipo === "salida" && !!dia.salida)}
            onClick={() => mutFichada.mutate()}
            data-testid="fm-cargar"
          >
            Cargar fichada manual
          </button>
        </div>
        {((fmTipo === "entrada" && dia.entrada) || (fmTipo === "salida" && dia.salida)) && (
          <p className="mt-2 text-[11px] text-gray-600">Ese día ya tiene {fmTipo}; para cambiarla, pedile a RRHH que la corrija desde la ficha.</p>
        )}
      </div>
    </Modal>
  );
}
